<template>
  <div class="providers-tab">
    <div class="pt-title">
      <v-icon color="primary" size="24">mdi-account-group-outline</v-icon>
      <h1>Providers</h1>
    </div>

    <v-skeleton-loader v-if="loading" type="card, card" />

    <v-alert v-else-if="listError" type="error" variant="tonal" class="mb-4">
      {{ listError }}
      <template #append>
        <v-btn variant="text" size="small" @click="loadAll()">Try again</v-btn>
      </template>
    </v-alert>

    <ProviderEmptyState v-else-if="!providers.length" @add="showAdd = true" />

    <template v-else>
      <ProviderHeaderCard
        :providers="providers"
        :selected-id="selectedId"
        :header="headerData"
        @select="selectProvider"
        @add="showAdd = true"
        @add-npi="showAddNpi = true"
      />

      <v-alert v-if="visibilityError" type="error" variant="tonal">
        {{ visibilityError }}
        <template #append>
          <v-btn variant="text" size="small" @click="reloadVisibility">Try again</v-btn>
        </template>
      </v-alert>

      <ProviderQuestionReview
        v-if="inSetup"
        :review-entity-id="reviewEntityId"
        :in-setup="false"
        @confirmed="onConfirmed"
        @skipped="onConfirmed"
      />

      <template v-else-if="visibility && !visibilityError">
        <v-alert v-if="visibility.questions_pending" type="info" variant="tonal" class="mb-4" data-testid="pq-arriving">
          We're asking AI about {{ serviceLine || 'your' }} providers{{ region ? ` in ${region}` : '' }}. This takes a few minutes.
        </v-alert>
        <ProviderScoreChart
          :daily="visibility.score?.daily || []"
          :today="visibility.score?.today ?? null"
          :provider-name="providerName"
        />
        <ProviderRankings
          :key="`${selectedId}-${selectedProvider}`"
          :personas="visibility.personas || []"
          :provider-name="providerName"
          :llm-provider="selectedProvider"
          :hospital="visibility.header?.hospital || ''"
          :sites="visibility.sites_used_most || []"
          @go-to-tab="$emit('go-to-tab', $event)"
        />
        <ProviderComparisonTable
          :key="`${selectedId}-${selectedProvider}`"
          :comparison="visibility.comparison"
          :provider-name="providerName"
          :question-count="questionCount"
          :pending="!!visibility.comparison_pending"
        />
      </template>
      <section v-else-if="!visibilityError" class="pt-placeholder">
        <v-skeleton-loader type="heading, image, list-item-three-line" />
      </section>
    </template>

    <AddProviderDialog
      v-model="showAdd"
      :review-entity-id="reviewEntityId"
      :hospital="lockedHospital"
      :service-line="serviceLine"
      @added="onChanged($event.id)"
    />
    <AddNpiDialog
      v-model="showAddNpi"
      :review-entity-id="reviewEntityId"
      :profile="selectedEntry"
      @assigned="onChanged($event.id)"
      @taken="onTaken"
    />
    <SameProviderDialog
      v-model="showSame"
      :review-entity-id="reviewEntityId"
      :profile="selectedEntry"
      :existing="existing"
      :audit-name="auditName"
      @different-npi="showSame = false; showAddNpi = true"
      @keep="showSame = false"
      @linked="onLinked"
    />
  </div>
</template>

<script>
import ProviderEmptyState from './ProviderEmptyState.vue'
import ProviderHeaderCard from './ProviderHeaderCard.vue'
import AddProviderDialog from './AddProviderDialog.vue'
import AddNpiDialog from './AddNpiDialog.vue'
import SameProviderDialog from './SameProviderDialog.vue'
import ProviderScoreChart from './ProviderScoreChart.vue'
import ProviderRankings from './ProviderRankings.vue'
import ProviderComparisonTable from './ProviderComparisonTable.vue'
import ProviderQuestionReview from './ProviderQuestionReview.vue'
import { listProviders, getVisibility } from './providersApi'

const POLL_MS = 10000
// While any audit's provider questions are being written, refresh the list this often, for this long.
// The cap is past ProviderQuestionSet::STUCK_AFTER (15 minutes), so the poll can reveal a stuck set.
const SETUP_POLL_MS = 5000
const SETUP_POLL_MAX_MS = 16 * 60 * 1000
const GROUNDED = 'google_grounded'

export default {
  name: 'ProvidersTab',
  components: { ProviderEmptyState, ProviderHeaderCard, AddProviderDialog, AddNpiDialog, SameProviderDialog, ProviderScoreChart, ProviderRankings, ProviderComparisonTable, ProviderQuestionReview },
  props: {
    reviewEntityId: { type: [Number, String], default: null },
    selectedProvider: { type: String, default: GROUNDED },
    // From the audit: the locked "Hospital and service line" row on the add dialog.
    hospital: { type: String, default: '' },
    serviceLine: { type: String, default: '' },
    region: { type: String, default: '' }
  },
  // providers-changed: a provider was added, got an NPI or was linked, so the
  // server's diagnosis setup (provider count, providers_pending) has changed.
  emits: ['providers-changed', 'providers-loaded', 'go-to-tab'],
  data() {
    return {
      loading: true,
      listError: '',
      visibilityError: '',
      providers: [],
      selectedId: null,
      visibility: null,
      showAdd: false,
      showAddNpi: false,
      showSame: false,
      existing: null,
      pollTimer: null,
      setupTimer: null,
      setupStartedAt: null,
      requestSeq: 0
    }
  },
  computed: {
    // The list row carries the profile id the add-NPI and merge calls need (the header doesn't).
    selectedEntry() {
      return this.providers.find(p => p.id === this.selectedId) || null
    },
    lockedHospital() {
      return this.providers.find(p => p.hospital)?.hospital || this.hospital
    },
    auditName() {
      return [this.hospital, this.serviceLine].filter(Boolean).join(' \u2013 ')
    },
    // The list row has the same header fields, so the card shows before visibility loads
    // and stays up if it fails.
    headerData() {
      if (this.visibility?.header) return this.visibility.header
      return this.providers.find(p => p.id === this.selectedId) || null
    },
    providerName() {
      return this.headerData?.name || 'this provider'
    },
    questionCount() {
      const n = (this.visibility?.personas || []).reduce((a, p) => a + (p.questions || []).length, 0)
      return n || 9
    },
    // The audit's provider questions aren't active yet (the same for every doctor), so show the
    // review above instead of results.
    setupStage() {
      return this.providers[0]?.setup_stage || null
    },
    inSetup() {
      return !!this.setupStage && this.setupStage !== 'active'
    },
    anyGenerating() {
      return this.setupStage === 'generating' && !this.providers[0]?.setup_stuck
    },
    pending() {
      return !!(this.visibility && (this.visibility.questions_pending || this.visibility.comparison_pending))
    }
  },
  watch: {
    providers(list) { this.$emit('providers-loaded', list) },
    reviewEntityId() {
      this.selectedId = null
      this.loadAll()
    },
    selectedProvider() {
      this.loadAll()
    },
    // The audit's set just went active (confirmed, or finished in the background): load results.
    inSetup(now, was) {
      if (was && !now) this.reloadVisibility()
    }
  },
  mounted() {
    document.addEventListener('visibilitychange', this.onVisibility)
    this.loadAll()
  },
  beforeUnmount() {
    this.requestSeq += 1
    this.stopPolling()
    this.stopSetupPolling()
    document.removeEventListener('visibilitychange', this.onVisibility)
  },
  methods: {
    // Reloads the list, keeps the current provider (or picks the first, or `selectId`),
    // then loads its visibility. Later tasks call this after adding a provider.
    // A list failure is full-page; a visibility failure stays inline under the header.
    async loadAll(selectId = null) {
      if (!this.reviewEntityId) return
      const seq = ++this.requestSeq
      this.stopPolling()
      this.stopSetupPolling()
      this.loading = true
      this.listError = ''
      this.visibilityError = ''
      this.visibility = null
      try {
        const res = await listProviders(this.reviewEntityId, this.selectedProvider)
        if (seq !== this.requestSeq) return
        this.providers = res.providers || []
      } catch (e) {
        if (seq !== this.requestSeq) return
        this.listError = e.response?.data?.error || "We couldn't load providers."
        this.loading = false
        return
      }
      const ids = this.providers.map(p => p.id)
      if (selectId && ids.includes(selectId)) this.selectedId = selectId
      else if (!ids.includes(this.selectedId)) this.selectedId = ids[0] ?? null
      this.loading = false
      this.setupStartedAt = null
      this.scheduleSetupPoll()
      if (this.selectedId != null && !this.inSetup) await this.fetchVisibility(seq)
    },
    // Confirmed (or skipped): the list now says active and the questions run. Refresh both.
    async onConfirmed() {
      this.$emit('providers-changed')
      await this.refreshList(true)
      return this.reloadVisibility()
    },
    onChanged(id) {
      this.$emit('providers-changed')
      return this.loadAll(id)
    },
    onTaken({ existing }) {
      this.existing = existing
      this.showAddNpi = false
      this.showSame = true
    },
    onLinked() {
      const id = this.existing?.id
      this.showSame = false
      this.$emit('providers-changed')
      return this.loadAll(id)
    },
    selectProvider(id) {
      if (id === this.selectedId) return
      this.selectedId = id
      return this.reloadVisibility()
    },
    reloadVisibility() {
      const seq = ++this.requestSeq
      this.stopPolling()
      this.visibility = null
      if (this.inSetup) return
      return this.fetchVisibility(seq)
    },
    async fetchVisibility(seq) {
      this.visibilityError = ''
      try {
        const res = await getVisibility(this.reviewEntityId, this.selectedId, this.selectedProvider)
        if (seq !== this.requestSeq) return
        this.visibility = res
        this.schedulePoll()
      } catch (e) {
        if (seq !== this.requestSeq) return
        this.visibilityError = e.response?.data?.error || "We couldn't load this provider."
      }
    },
    schedulePoll() {
      this.stopPolling()
      if (!this.pending || document.hidden) return
      this.pollTimer = setTimeout(this.poll, POLL_MS)
    },
    async poll() {
      this.pollTimer = null
      if (document.hidden || !this.pending) return
      const seq = this.requestSeq
      try {
        const [list, vis] = await Promise.all([
          listProviders(this.reviewEntityId, this.selectedProvider),
          getVisibility(this.reviewEntityId, this.selectedId, this.selectedProvider)
        ])
        if (seq !== this.requestSeq) return
        this.providers = list.providers || this.providers
        this.visibility = vis
      } catch (e) {
        if (seq !== this.requestSeq) return
        // transient failure: keep what we have and try again
      }
      this.schedulePoll()
    },
    stopPolling() {
      if (this.pollTimer) clearTimeout(this.pollTimer)
      this.pollTimer = null
    },
    onVisibility() {
      if (document.hidden) {
        this.stopPolling()
        this.stopSetupPolling()
      } else {
        if (this.pending && !this.pollTimer) this.schedulePoll()
        this.scheduleSetupPoll()
      }
    },
    // Quietly reloads the list (no skeleton), so open review forms stay as they are.
    // `restart` is true after the user acts, which gives a fresh 16 minutes of polling.
    async refreshList(restart = false) {
      const reId = this.reviewEntityId
      if (restart) this.setupStartedAt = null
      try {
        const res = await listProviders(reId, this.selectedProvider)
        if (reId !== this.reviewEntityId) return
        this.providers = res.providers || this.providers
      } catch (e) {
        // transient: keep what we have
      }
      this.scheduleSetupPoll()
    },
    scheduleSetupPoll() {
      if (this.setupTimer) return
      if (!this.anyGenerating) { this.setupStartedAt = null; return }
      if (document.hidden) return
      if (this.setupStartedAt == null) this.setupStartedAt = Date.now()
      if (Date.now() - this.setupStartedAt >= SETUP_POLL_MAX_MS) return
      this.setupTimer = setTimeout(async () => {
        this.setupTimer = null
        await this.refreshList()
      }, SETUP_POLL_MS)
    },
    stopSetupPolling() {
      if (this.setupTimer) clearTimeout(this.setupTimer)
      this.setupTimer = null
    }
  }
}
</script>

<style scoped>
.providers-tab {
  max-width: 1344px;
  width: 100%;
  margin: 0 auto;
  padding: 24px 32px 48px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: var(--brand-ink-950);
}
.pt-title { display: flex; align-items: center; gap: 12px; }
.pt-title h1 { margin: 0; font-family: inherit; font-size: 24px; font-weight: 700; }
.pt-placeholder {
  background: #fff;
  border: 1px solid #e6e1d4;
  border-radius: 12px;
  padding: 20px 28px;
  font-weight: 600;
}
@media (max-width: 600px) {
  .providers-tab { padding: 16px; }
}
</style>
